<template>
  <div class="daily">
    <v-overlay :value="isLoading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <v-container>
      <DailyFilters
        v-on:search="search"
        :filterDisabled="!orders.length"
        :orders="orders"
        :kitchens="kitchens"
        :concepts="concepts"
        :platforms="platforms"
        :countries="countries"
        v-on:apply="apply"
      />
      <DailyCountriesFigures :orders="filteredOrders" :lastWeekOrders="filteredLastWeekOrders" />
      <DailyConceptsFigures
        :orders="filteredOrders"
        :lastWeekOrders="filteredLastWeekOrders"
        :concepts="concepts"
      />
      <v-row v-if="filteredOrders.length">
        <v-col cols="12" md="4">
          <v-card>
            <v-card-title>
              <span>Split by kitchen</span>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon @click="kitchenChartDisplay = !kitchenChartDisplay">
                    <v-icon v-bind="attrs" v-on="on">mdi-swap-horizontal</v-icon>
                  </v-btn>
                </template>
                <span>See data</span>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <DailyFilterPieChart
                :orders="filteredOrders"
                :filter="kitchens"
                field="id"
                orderField="kitchen_uuid"
                elementField="uuid"
                v-if="kitchenChartDisplay"
              />
              <DailyFilterTable
                :orders="filteredOrders"
                :lastWeekOrders="filteredLastWeekOrders"
                :filter="kitchens"
                field="id"
                orderField="kitchen_uuid"
                elementField="uuid"
                filterName="Kitchen"
                v-else
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-title>
              <span>Split by concept</span>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon @click="conceptChartDisplay = !conceptChartDisplay">
                    <v-icon v-bind="attrs" v-on="on">mdi-swap-horizontal</v-icon>
                  </v-btn>
                </template>
                <span>See data</span>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <DailyFilterPieChart
                :orders="filteredOrders"
                :filter="concepts"
                field="label"
                orderField="concept_uuid"
                elementField="uuid"
                v-if="conceptChartDisplay"
              />
              <DailyFilterTable
                :orders="filteredOrders"
                :lastWeekOrders="filteredLastWeekOrders"
                :filter="concepts"
                field="label"
                orderField="concept_uuid"
                elementField="uuid"
                filterName="Concept"
                v-else
              />
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card>
            <v-card-title>
              <span>Split by country</span>
              <v-spacer></v-spacer>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon @click="countryChartDisplay = !countryChartDisplay">
                    <v-icon v-bind="attrs" v-on="on">mdi-swap-horizontal</v-icon>
                  </v-btn>
                </template>
                <span>See data</span>
              </v-tooltip>
            </v-card-title>
            <v-card-text>
              <DailyFilterPieChart
                :orders="filteredOrders"
                :filter="countries"
                field="id"
                orderField="country_code"
                elementField="id"
                v-if="countryChartDisplay"
              />
              <DailyFilterTable
                :orders="filteredOrders"
                :lastWeekOrders="filteredLastWeekOrders"
                :filter="countries"
                field="id"
                orderField="country_code"
                elementField="id"
                filterName="Country"
                v-else
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <DailyPlatformsFigures
        :orders="filteredOrders"
        :lastWeekOrders="filteredLastWeekOrders"
        :platforms="platforms"
      />
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import _ from "lodash";
import moment from "moment";
import DailyFilters from "@/components/Analyze/DailyFilters";
import DailyConceptsFigures from "@/components/Analyze/DailyConceptsFigures";
import DailyCountriesFigures from "@/components/Analyze/DailyCountriesFigures";
import DailyPlatformsFigures from "@/components/Analyze/DailyPlatformsFigures";
import DailyFilterPieChart from "@/components/Analyze/DailyFilterPieChart";
import DailyFilterTable from "@/components/Analyze/DailyFilterTable";

export default {
  components: {
    DailyFilters,
    DailyConceptsFigures,
    DailyCountriesFigures,
    DailyPlatformsFigures,
    DailyFilterPieChart,
    DailyFilterTable,
  },
  data: () => {
    return {
      selectedKitchens: [],
      selectedConcepts: [],
      selectedPlatforms: [],
      selectedCountries: [],
      selectedStatuses: [],
      selectedShifts: [],
      orders: [],
      lastWeekOrders: [],
      kitchenChartDisplay: true,
      conceptChartDisplay: true,
      countryChartDisplay: true,
    };
  },
  computed: {
    ...mapState({
      kitchens: (state) => state.restaurant.kitchens,
      concepts: (state) => state.restaurant.concepts,
      platforms: (state) => state.restaurant.platforms,
      countries: (state) => state.restaurant.countries,
      kitchensLoading: (state) => state.restaurant.fetchAllKitchens.pending,
      conceptsLoading: (state) => state.restaurant.fetchAllConcepts.pending,
      platformsLoading: (state) => state.restaurant.fetchAllPlatforms.pending,
      countriesLoading: (state) => state.restaurant.fetchAllCountries.pending,
      ordersLoading: (state) => state.orders.fetchAll.pending,
    }),
    isLoading() {
      return (
        this.kitchensLoading ||
        this.conceptsLoading ||
        this.platformsLoading ||
        this.countriesLoading ||
        this.ordersLoading
      );
    },
    filteredOrders() {
      return this.filterOrders(this.orders);
    },
    filteredLastWeekOrders() {
      return this.filterOrders(this.lastWeekOrders);
    },
    ordersByKitchen() {
      return _.chain(this.filteredOrders).groupBy("kitchen_uuid").value();
    },
  },
  methods: {
    ...mapActions({
      setError: "setError",
    }),
    ...mapActions("restaurant", {
      fetchAllConcepts: "fetchAllConcepts",
      fetchAllKitchens: "fetchAllKitchens",
      fetchAllPlatforms: "fetchAllPlatforms",
      fetchAllCountries: "fetchAllCountries",
    }),
    ...mapActions("orders", {
      fetchAllOrders: "fetchAll",
    }),
    search(date) {
      let lastWeekDate = moment(date).subtract(7, "days").format("YYYY-MM-DD");
      this.fetchAllOrders({ day: date, summary: true })
        .then((data) => {
          this.orders = data;
        })
        .catch((error) => {
          this.setError(error);
        });
      this.fetchAllOrders({ day: lastWeekDate, summary: true })
        .then((data) => {
          this.lastWeekOrders = data;
        })
        .catch((error) => {
          this.setError(error);
        });
    },
    apply(kitchens, concepts, platforms, countries, statuses, shifts) {
      this.selectedKitchens = kitchens;
      this.selectedConcepts = concepts;
      this.selectedPlatforms = platforms;
      this.selectedCountries = countries;
      this.selectedStatuses = statuses;
      this.selectedShifts = shifts;
    },
    filterOrders(orders) {
      return orders
        .filter((order) => {
          return (
            !this.selectedKitchens.length ||
            this.selectedKitchens.includes(order.kitchen_uuid)
          );
        })
        .filter((order) => {
          return (
            !this.selectedConcepts.length ||
            this.selectedConcepts.includes(order.concept_uuid)
          );
        })
        .filter((order) => {
          return (
            !this.selectedPlatforms.length ||
            this.selectedPlatforms.includes(order.platform_uuid)
          );
        })
        .filter((order) => {
          return (
            !this.selectedCountries.length ||
            this.selectedCountries.includes(order.country_code)
          );
        })
        .filter((order) => {
          return (
            !this.selectedStatuses.length ||
            this.selectedStatuses.includes(order.status)
          );
        })
        .filter((order) => {
          return (
            !this.selectedShifts.length ||
            this.selectedShifts.includes(order.shift)
          );
        });
    },
  },
  created() {
    this.fetchAllConcepts().catch((error) => {
      this.setError(error);
    });
    this.fetchAllKitchens().catch((error) => {
      this.setError(error);
    });
    this.fetchAllPlatforms().catch((error) => {
      this.setError(error);
    });
    this.fetchAllCountries().catch((error) => {
      this.setError(error);
    });
  },
};
</script>
